import { useCallback } from "react"
import { useConfig } from "@/config/context"
import { buildNoteTags, withClientTag } from "@/nostr/api/feed"
import {
  buildDeleteDraft,
  buildProfileDraft,
  buildReactionDraft,
  buildRelayListDraft,
  buildRepostDraft,
  type RelayListEntry,
} from "@/nostr/api/social"
import { buildRelayReviewDraft } from "@/nostr/api/relayReviews"
import type { DraftEvent, NostrEvent, PublishSummary } from "@/nostr/types"
import { KINDS } from "@/nostr/types"
import { useAuth } from "@/state/AuthContext"
import { summarizePublish, useRelays } from "@/state/RelayContext"
import { useNostr } from "@/state/NostrContext"
import { useToast } from "@/state/ToastContext"
import { nowSeconds } from "@/utils/time"

export interface PublishOutcome {
  ok: boolean
  summary?: PublishSummary
  error?: string
  /** The signed event, available after a successful publish (used for threads). */
  event?: NostrEvent
}

export interface PublishOptions {
  relays?: string[]
  successMessage?: string
  silent?: boolean
}

/**
 * The single write path of the application: sign with the session signer, then
 * publish to the user's relays and report the aggregated OK responses.
 *
 * Nothing here reports success before at least one relay accepted the event.
 */
export function usePublish() {
  const config = useConfig()
  const { pool } = useNostr()
  const { signer } = useAuth()
  const { writeTargets } = useRelays()
  const { push } = useToast()

  const publishDraft = useCallback(
    async (draft: DraftEvent, options: PublishOptions = {}): Promise<PublishOutcome> => {
      if (!signer) {
        push("error", "Log in with a signer to publish")
        return { ok: false, error: "no signer" }
      }
      const relays = options.relays && options.relays.length > 0 ? options.relays : writeTargets
      if (relays.length === 0) {
        push("error", "Add at least one relay before publishing")
        return { ok: false, error: "no relays" }
      }

      try {
        const signed = await signer.signEvent({
          ...draft,
          tags: withClientTag(draft.tags, config.branding.name),
        })
        const summary = await pool.publish(signed, relays)
        if (summary.accepted === 0) {
          push("error", "No relay accepted the event", summarizePublish(summary))
          return { ok: false, summary, error: "rejected" }
        }
        if (!options.silent) {
          const suffix = summary.rejected > 0 ? ` (${summary.accepted}/${summary.results.length} relays)` : ""
          push("success", `${options.successMessage ?? "Published"}${suffix}`)
        }
        return { ok: true, summary, event: signed }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        push("error", "Could not sign the event", message)
        return { ok: false, error: message }
      }
    },
    [signer, pool, writeTargets, push, config.branding.name],
  )

  const publishNote = useCallback(
    async (
      content: string,
      options: {
        replyTo?: NostrEvent
        root?: NostrEvent
        relays?: string[]
        kind?: number
        /** Extra tags appended after the NIP-10 tags (NIP-70 `-`, NIP-36 warning, …). */
        tags?: string[][]
        silent?: boolean
      } = {},
    ) => {
      const trimmed = content.trim()
      if (!trimmed) return { ok: false, error: "empty" } satisfies PublishOutcome
      const draft: DraftEvent = {
        kind: options.kind ?? KINDS.NOTE,
        content: trimmed,
        tags: [
          ...buildNoteTags({
            content: trimmed,
            replyTo: options.replyTo,
            root: options.root,
          }),
          ...(options.tags ?? []),
        ],
        created_at: nowSeconds(),
      }
      return publishDraft(draft, {
        relays: options.relays,
        silent: options.silent,
        successMessage: options.replyTo ? "Reply published" : "Note published",
      })
    },
    [publishDraft],
  )

  const react = useCallback(
    async (note: NostrEvent, emoji = "+") => publishDraft(buildReactionDraft(note, emoji), { successMessage: "Reaction published" }),
    [publishDraft],
  )

  const repost = useCallback(
    async (note: NostrEvent) => publishDraft(buildRepostDraft(note), { successMessage: "Reposted" }),
    [publishDraft],
  )

  const deleteEvent = useCallback(
    async (eventId: string, reason = "") =>
      publishDraft(buildDeleteDraft([eventId], reason), { successMessage: "Deletion request published" }),
    [publishDraft],
  )

  const reviewRelay = useCallback(
    async (relayUrl: string, content: string, stars: number) =>
      publishDraft(buildRelayReviewDraft(relayUrl, content, stars), {
        successMessage: "Relay review published",
      }),
    [publishDraft],
  )

  const updateProfile = useCallback(
    async (metadata: Record<string, string | undefined>, previous: Record<string, unknown> = {}, pubkey?: string) =>
      publishDraft(buildProfileDraft(metadata, previous, pubkey), { successMessage: "Profile saved" }),
    [publishDraft],
  )

  const updateRelayList = useCallback(
    async (entries: RelayListEntry[], previousContent = "") =>
      publishDraft(buildRelayListDraft(entries, previousContent), {
        successMessage: "Relay list published",
      }),
    [publishDraft],
  )

  return { publishDraft, publishNote, react, repost, deleteEvent, reviewRelay, updateProfile, updateRelayList }
}
